/**
 * ============================================================================
 * СЕРВИС ТЕСТОВ
 * ============================================================================
 * Методы для работы с тестами и вопросами
 * ============================================================================
 */

import api from './api'
import { compressImage, shouldCompress } from '@/utils/imageCompression'

export const testsService = {
	/**
	 * Получить все тесты модуля
	 * @param {number} moduleId - ID модуля
	 * @param {boolean} includeUnpublished - Включать ли неопубликованные тесты (по умолчанию true для преподавателей)
	 */
	async getByModuleId(moduleId, includeUnpublished = true) {
		const response = await api.get(`/modules/${moduleId}/tests`, {
			params: { includeUnpublished },
		})
		return response.data
	},

	/**
	 * Получить тест по ID
	 */
	async getById(id) {
		const response = await api.get(`/tests/${id}`)
		return response.data
	},

	/**
	 * Получить тест с вопросами
	 */
	async getWithQuestions(id) {
		// Роут GET /tests/:id возвращает тест с вопросами
		const response = await api.get(`/tests/${id}`)
		return response.data
	},

	/**
	 * Создать новый тест
	 */
	async create(moduleId, testData) {
		const response = await api.post(`/modules/${moduleId}/tests`, testData)
		return response.data
	},

	/**
	 * Обновить тест
	 */
	async update(id, testData) {
		const response = await api.put(`/tests/${id}`, testData)
		return response.data
	},

	/**
	 * Удалить тест
	 */
	async delete(id) {
		const response = await api.delete(`/tests/${id}`)
		return response.data
	},

	/**
	 * Опубликовать тест
	 */
	async publish(id) {
		const response = await api.post(`/tests/${id}/publish`)
		return response.data
	},

	/**
	 * Добавить вопрос к тесту
	 */
	async addQuestion(testId, questionData) {
		const response = await api.post(`/tests/${testId}/questions`, questionData)
		return response.data
	},

	/**
	 * Обновить вопрос
	 */
	async updateQuestion(testId, questionId, questionData) {
		const response = await api.put(
			`/tests/${testId}/questions/${questionId}`,
			questionData
		)
		return response.data
	},

	/**
	 * Удалить вопрос
	 */
	async deleteQuestion(testId, questionId) {
		const response = await api.delete(
			`/tests/${testId}/questions/${questionId}`
		)
		return response.data
	},

	/**
	 * Добавить ответ к вопросу
	 */
	async addAnswer(testId, questionId, answerData) {
		const response = await api.post(
			`/tests/${testId}/questions/${questionId}/answers`,
			answerData
		)
		return response.data
	},

	/**
	 * Загрузить изображение для вопроса
	 */
	async uploadQuestionImage(questionId, file, onUploadProgress) {
		// Сжимаем изображение если оно больше 1MB
		let fileToUpload = file
		if (shouldCompress(file, 1)) {
			console.log(`🗜️ Сжатие изображения вопроса: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
			fileToUpload = await compressImage(file, {
				maxWidth: 1920,
				maxHeight: 1080,
				quality: 0.85,
				maxSizeMB: 1,
			})
			console.log(`✅ Сжато до: ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`)
		}

		const formData = new FormData()
		formData.append('image', fileToUpload)

		const response = await api.post(
			`/tests/questions/${questionId}/image`,
			formData,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
				onUploadProgress,
			}
		)
		return response.data
	},

	/**
	 * Загрузить изображение для ответа
	 */
	async uploadAnswerImage(answerId, file, onUploadProgress) {
		// Сжимаем изображение если оно больше 1MB
		let fileToUpload = file
		if (shouldCompress(file, 1)) {
			console.log(`🗜️ Сжатие изображения ответа: ${(file.size / 1024 / 1024).toFixed(2)}MB`)
			fileToUpload = await compressImage(file, {
				maxWidth: 1920,
				maxHeight: 1080,
				quality: 0.85,
				maxSizeMB: 1,
			})
			console.log(`✅ Сжато до: ${(fileToUpload.size / 1024 / 1024).toFixed(2)}MB`)
		}

		const formData = new FormData()
		formData.append('image', fileToUpload)

		const response = await api.post(
			`/tests/answers/${answerId}/image`,
			formData,
			{
				headers: { 'Content-Type': 'multipart/form-data' },
				onUploadProgress,
			}
		)
		return response.data
	},

	/**
	 * Удалить изображение вопроса
	 */
	async deleteQuestionImage(questionId) {
		const response = await api.delete(`/tests/questions/${questionId}/image`)
		return response.data
	},

	/**
	 * Удалить изображение ответа
	 */
	async deleteAnswerImage(answerId) {
		const response = await api.delete(`/tests/answers/${answerId}/image`)
		return response.data
	},

	/**
	 * Начать попытку прохождения теста
	 */
	async startAttempt(testId) {
		const response = await api.post(`/tests/${testId}/start`)
		return response.data
	},

	/**
	 * Завершить попытку и отправить ответы
	 */
	async submitAttempt(testId, attemptId, answers, timeSpentSeconds = null) {
		const payload = { answers }
		if (timeSpentSeconds !== null && timeSpentSeconds !== undefined) {
			payload.time_spent_seconds = timeSpentSeconds
		}
		const response = await api.post(`/tests/attempts/${attemptId}/complete`, payload)
		return response.data
	},

	/**
	 * Получить результаты попытки
	 */
	async getAttemptResults(testId, attemptId) {
		const response = await api.get(`/tests/${testId}/attempts/${attemptId}`)
		return response.data
	},

	/**
	 * Получить все попытки студента
	 */
	async getStudentAttempts(testId) {
		const response = await api.get(`/tests/${testId}/attempts/my`)
		return response.data
	},

	/**
	 * Получить все попытки по тесту (для преподавателя)
	 */
	async getAttempts(testId) {
		const response = await api.get(`/tests/${testId}/attempts`)
		return response?.data ?? response
	},

	/**
	 * Оценить попытку теста (преподаватель)
	 */
	async gradeAttempt(attemptId, payload) {
		const response = await api.patch(`/tests/attempts/${attemptId}/grade`, payload)
		return response?.data ?? response
	},
}

export default testsService
